<template>
  <van-popup
    v-model:show="visible"
    position="center"
    round
    :style="{ background: 'transparent', padding: '0' }"
    :close-on-click-overlay="closeOnClickOverlay"
    @closed="handleClosed"
    destroy-on-close
  >
    <div class="base-modal">
      <!-- Close button -->
      <button
        v-if="showClose"
        class="close-btn"
        @click="handleClose"
        aria-label="Close"
      >
        <img src="@/assets/images/icons/close.svg" alt="Close Button" />
      </button>

      <!-- Title -->
      <h2 v-if="title" class="modal-title">{{ title }}</h2>

      <!-- Main content -->
      <div class="modal-content">
        <slot></slot>
      </div>

      <!-- Confirm Button -->
      <button
        v-if="showConfirm"
        class="confirm-btn"
        :disabled="confirmDisabled"
        @click="handleConfirm"
      >
        {{ confirmText }}
      </button>

      <!-- Footer slot -->
      <div v-if="$slots.footer" class="modal-footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: true,
  },
  showConfirm: {
    type: Boolean,
    default: false,
  },
  confirmText: {
    type: String,
    default: 'Confirm',
  },
  confirmDisabled: {
    type: Boolean,
    default: false,
  },
  transitionAppear: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:show', 'close', 'closed', 'confirm'])

const visible = ref(props.show)

// Watch props.show to sync with visible
watch(
  () => props.show,
  (newVal) => {
    visible.value = newVal
  },
  { immediate: true }
)

// Watch visible to emit update:show
watch(visible, (newVal) => {
  emit('update:show', newVal)
})

function handleClose() {
  emit('close')
}

function handleClosed() {
  emit('closed')
}

function handleConfirm() {
  emit('confirm')
}
</script>

<style scoped lang="less">
.base-modal {
  position: relative;
  overflow: hidden;
  width: 291px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f1e0ff 0%, #ffffff 73.63%);
  padding: 20px 24px;

  // Close button
  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
    }

    &:active {
      opacity: 0.7;
    }
  }

  // Title
  .modal-title {
    margin-bottom: 28px;
    color: #29244C;
    text-align: center;
    font-family: "PingFang SC";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  // Confirm Button
  .confirm-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(170.8deg, #c764ff 3.02%, #7f4aff 96.08%);
    border: none;
    border-radius: 61px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.3s ease;

    &:active {
      transform: scale(0.98);
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
